/**
 * Root layout shell — sidebar + header + content area.
 *
 * Reads `app.slowFeaturesReveal` from config to determine which sidebar
 * items to show. Components are split into:
 *   - SidebarLogo.tsx   — VERINT brand text
 *   - SidebarFull.tsx   — all navigation items
 *   - SidebarPartial.tsx — feature-flagged subset
 *   - PortalHeader.tsx  — route-aware page header
 *   - UserMenu.tsx      — avatar + sign-out dropdown
 */

import { PropsWithChildren, useEffect } from 'react';
import { Box, makeStyles } from '@material-ui/core';
import { useLocation } from 'react-router-dom';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import { Settings as SidebarSettings, UserSettingsSignInAvatar } from '@backstage/plugin-user-settings';
import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarPage,
  SidebarSpace,
} from '@backstage/core-components';
import { SidebarFull } from './SidebarFull';
import { SidebarPartial } from './SidebarPartial';
import { SidebarLogo } from './SidebarLogo';
import { PortalHeader } from './PortalHeader';

const useSidebarStyles = makeStyles(theme => {
  const isDark = theme.palette.type === 'dark';
  const sidebarBg = isDark ? '#161618' : '#ffffff';
  const sidebarBorder = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)';
  const itemColor = isDark ? '#b0b4b8' : '#1c1c1e';
  const itemHoverBg = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';
  const itemHoverColor = isDark ? '#ffffff' : '#000000';
  const iconColor = isDark ? '#6e7479' : '#48484a';
  const dividerColor = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.06)';
  const labelColor = isDark ? '#505458' : '#86868b';
  const scrollThumb = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)';

  return {
    '@global': {
      // Prevent document-level scrollbar — all scrolling happens in contentArea
      'html, body, #root': {
        overflow: 'hidden !important',
        height: '100% !important',
        margin: '0 !important',
        padding: '0 !important',
      },
      // Prevent SidebarPage from creating scrollable overflow
      '#root > div': {
        overflow: 'hidden !important',
      },
      // BackstagePage is 100vh, but it sits below PortalHeader inside contentArea,
      // so it overhangs by the header's height and every page gains a strip of
      // empty scroll underneath it.
      '[class*="BackstagePage-root"]': {
        height: '100% !important',
      },
      // Selected submenu items: OKD Consoles + Hydra Tools
      'a[href="/okd-consoles"][aria-current="page"], a[href="/our-tools-menu"][aria-current="page"], a[href="/okd-consoles"][class*="selected"], a[href="/our-tools-menu"][class*="selected"], a[href="/okd-consoles"][class*="Selected"], a[href="/our-tools-menu"][class*="Selected"]': {
        boxSizing: 'border-box',
        width: '100% !important',
        margin: '0 !important',
        position: 'relative',
        overflow: 'hidden',
        padding: '9px 14px !important',
        borderRadius: '0 !important',
        backgroundColor: isDark
          ? 'rgba(77,163,255,0.16) !important'
          : 'rgba(0,120,212,0.12) !important',

        color: `${itemColor} !important`,
        fontWeight: '500 !important',
      },
      // Left blue selected indicator for submenu items
      'a[href="/okd-consoles"][aria-current="page"]::before, a[href="/our-tools-menu"][aria-current="page"]::before, a[href="/okd-consoles"][class*="selected"]::before, a[href="/our-tools-menu"][class*="selected"]::before, a[href="/okd-consoles"][class*="Selected"]::before, a[href="/our-tools-menu"][class*="Selected"]::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: 3,
        backgroundColor: isDark
          ? 'rgba(77,163,255,0.8) !important'
          : 'rgba(0,120,212,0.75) !important',
      },
      // Keep submenu selected item blue on hover
      'a[href="/okd-consoles"][aria-current="page"]:hover, a[href="/our-tools-menu"][aria-current="page"]:hover, a[href="/okd-consoles"][class*="selected"]:hover, a[href="/our-tools-menu"][class*="selected"]:hover, a[href="/okd-consoles"][class*="Selected"]:hover, a[href="/our-tools-menu"][class*="Selected"]:hover': {
        backgroundColor: isDark
          ? 'rgba(77,163,255,0.16) !important'
          : 'rgba(0,120,212,0.12) !important',

        color: `${itemColor} !important`,
      },
      // Submenu selected item text/icon
      'a[href="/okd-consoles"][aria-current="page"] *, a[href="/our-tools-menu"][aria-current="page"] *, a[href="/okd-consoles"][class*="selected"] *, a[href="/our-tools-menu"][class*="selected"] *, a[href="/okd-consoles"][class*="Selected"] *, a[href="/our-tools-menu"][class*="Selected"] *': {
        color: `${itemColor} !important`,
        backgroundColor: 'transparent !important',
      },
      'a[href="/okd-consoles"][aria-current="page"] svg, a[href="/our-tools-menu"][aria-current="page"] svg, a[href="/okd-consoles"][class*="selected"] svg, a[href="/our-tools-menu"][class*="selected"] svg, a[href="/okd-consoles"][class*="Selected"] svg, a[href="/our-tools-menu"][class*="Selected"] svg': {
        color: `${iconColor} !important`,
      },
    },

    sidebarWrapper: {
      '& nav': {
        background: `${sidebarBg} !important`,
      },
      '& .MuiDrawer-paper': {
        background: `${sidebarBg} !important`,
      },
      '& [class*="BackstageSidebar"]': {
        background: `${sidebarBg} !important`,
      },

      // minified classes. Force light bg on sidebar items (bg only, no sizing).
      '& nav a:not([href="/search"]):not([href="/"])': {
        backgroundColor: `${sidebarBg} !important`,
      },
      // Force all item children transparent so parent bg shows uniformly
      '& nav a:not([href="/search"]):not([href="/"]) *': {
        backgroundColor: 'transparent !important',
      },
      '&& nav a:not([href="/search"]):not([href="/"]):hover': {
        backgroundColor: `${itemHoverBg} !important`,
        color: `${itemHoverColor} !important`,
      },
      '&& nav a:not([href="/search"]):not([href="/"]):hover svg': {
        color: `${itemHoverColor} !important`,
      },
      '& [data-testid="item-with-submenu"]': {
        backgroundColor: 'transparent !important',
      },
      '& [data-testid="item-with-submenu"] > div': {
        backgroundColor: `${sidebarBg} !important`,
      },
      '& [data-testid="item-with-submenu"] > div div': {
        backgroundColor: `${sidebarBg} !important`,
      },
      '& [data-testid="item-with-submenu"] > div ul': {
        backgroundColor: `${sidebarBg} !important`,
      },
      '& [data-testid="item-with-submenu"] > div li': {
        backgroundColor: `${sidebarBg} !important`,
      },
      '& [data-testid="item-with-submenu"] > div a': {
        backgroundColor: `${sidebarBg} !important`,
        color: `${itemColor} !important`,
      },
      '& [data-testid="item-with-submenu"] > div a *': {
        backgroundColor: 'transparent !important',
        color: 'inherit !important',
      },
      '&& [data-testid="item-with-submenu"] > div a:hover': {
        backgroundColor: `${itemHoverBg} !important`,
        color: `${itemHoverColor} !important`,
      },
      '&& [data-testid="item-with-submenu"] > div a:hover svg': {
        color: `${itemHoverColor} !important`,
      },
      // Submenu selected state
      '&& [data-testid="item-with-submenu"] > div a[class*="selected"], && [data-testid="item-with-submenu"] > div a[aria-current="page"]': {
        backgroundColor: isDark
          ? 'rgba(77,163,255,0.16) !important'
          : 'rgba(0,120,212,0.12) !important',
        color: `${itemColor} !important`,
      },
      '&& [data-testid="item-with-submenu"] > div a[class*="selected"]:hover, && [data-testid="item-with-submenu"] > div a[aria-current="page"]:hover': {
        backgroundColor: isDark
          ? 'rgba(77,163,255,0.22) !important'
          : 'rgba(0,120,212,0.16) !important',
      },

      // Search item — rounded rectangle
      '&& nav a[href="/search"]': {
        boxSizing: 'border-box',
        width: 'calc(100% - 24px) !important',
        margin: '1px 12px !important',
        border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.12)'
          } !important`,
        borderRadius: '8px !important',
        backgroundColor: isDark
          ? 'rgba(255,255,255,0.03) !important'
          : 'rgba(0,0,0,0.02) !important',
        overflow: 'hidden',
      },
      // Make all inner layers transparent so no white/gray mix appears
      '&& nav a[href="/search"] *, && nav a[href="/search"] *::before, && nav a[href="/search"] *::after': {
        backgroundColor: 'transparent !important',
      },
      // Inner MUI ListItem should inherit shape and spacing
      '&& nav a[href="/search"] .MuiListItem-root': {
        borderRadius: '8px !important',
        backgroundColor: 'transparent !important',
      },
      // Prevent hover from adding another background layer
      '&& nav a[href="/search"]:hover': {
        backgroundColor: isDark
          ? 'rgba(255,255,255,0.05) !important'
          : 'rgba(0,0,0,0.04) !important',
      },

      '&& nav a[href="/search"]:hover *, && nav a[href="/search"]:hover *::before, && nav a[href="/search"]:hover *::after': {
        backgroundColor: 'transparent !important',
      },

      // Prevent active/selected route from creating white/blue inner patches
      '&& nav a[href="/search"][aria-current="page"], && nav a[href="/search"].Mui-selected': {
        backgroundColor: isDark
          ? 'rgba(255,255,255,0.03) !important'
          : 'rgba(0,0,0,0.02) !important',
      },

      '&& nav a[href="/search"][aria-current="page"] *, && nav a[href="/search"].Mui-selected *': {
        backgroundColor: 'transparent !important',
      },

      // Sidebar items
      '& nav [class*="MuiListItem"], & nav [class*="MuiButtonBase"], & nav a[class*="MuiButtonBase"], & nav button[class*="MuiButtonBase"]': {
        borderRadius: '10px !important',
        margin: '1px 12px !important',
        padding: '9px 14px !important',
        transition: 'all 0.18s cubic-bezier(0.2, 0, 0, 1) !important',
        position: 'relative' as const,
        color: `${itemColor} !important`,
        fontWeight: '500 !important' as any,
        '&:hover': {
          backgroundColor: `${itemHoverBg} !important`,
          color: `${itemHoverColor} !important`,
          '& svg': {
            color: `${itemHoverColor} !important`,
          },
        },
        '& svg': {
          color: `${iconColor} !important`,
          fontSize: '20px !important',
          transition: 'color 0.18s cubic-bezier(0.2, 0, 0, 1) !important',
        },
      },

      // Active / selected item
      '&& nav a[class*="BackstageSidebarItem-selected"]:not([href="/search"])': {
        boxSizing: 'border-box',
        width: '100% !important',
        margin: '0 !important',
        borderRadius: '0 !important',
        backgroundColor: isDark
          ? 'rgba(77,163,255,0.16) !important'
          : 'rgba(0,120,212,0.12) !important',

        color: `${itemColor} !important`,
        overflow: 'hidden',
      },
      // Selected item label
      '&& nav a[class*="BackstageSidebarItem-selected"]:not([href="/search"]) [class*="BackstageSidebarItem-label"]': {
        color: `${itemColor} !important`,
        fontWeight: '500 !important',
      },
      // Selected item icon
      '&& nav a[class*="BackstageSidebarItem-selected"]:not([href="/search"]) svg': {
        color: `${iconColor} !important`,
      },
      // Remove inner background patches if any
      '&& nav a[class*="BackstageSidebarItem-selected"]:not([href="/search"]) *': {
        backgroundColor: 'transparent !important',
      },
      // Keep selected item full-width blue even on hover
      '&& nav a[class*="BackstageSidebarItem-selected"]:not([href="/search"]):hover': {
        backgroundColor: isDark
          ? 'rgba(77,163,255,0.16) !important'
          : 'rgba(0,120,212,0.12) !important',
      },

      '&&& nav a[aria-current="page"]:not([href="/search"]):not([href="/"])': {
        backgroundColor: isDark
          ? 'rgba(77,163,255,0.16) !important'
          : 'rgba(0,120,212,0.12) !important',
      },
      '&&& nav a[aria-current="page"]:not([href="/search"]):not([href="/"]):hover': {
        backgroundColor: isDark
          ? 'rgba(77,163,255,0.16) !important'
          : 'rgba(0,120,212,0.12) !important',
      },

      // Sidebar item text
      '& nav [class*="MuiListItemText"]': {
        '& span': {
          fontSize: '13.5px !important',
          fontWeight: 'inherit !important' as any,
          letterSpacing: '-0.01em !important',
          color: 'inherit !important',
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Roboto, sans-serif !important',
        },
      },

      // Sidebar item icons
      '& nav [class*="MuiListItemIcon"]': {
        color: `${iconColor} !important`,
        minWidth: '36px !important',
      },

      // Dividers
      '& nav hr': {
        border: 'none !important',
        borderTop: `1px solid ${dividerColor} !important`,
        margin: '10px 20px !important',
        opacity: '1 !important' as any,
        backgroundColor: 'transparent !important',
        height: '0 !important',
      },

      // Group labels
      '& nav [class*="SidebarGroup"] [class*="label"], & nav [class*="groupLabel"]': {
        fontSize: '10.5px !important',
        fontWeight: '600 !important' as any,
        letterSpacing: '0.06em !important',
        textTransform: 'uppercase' as const,
        color: `${labelColor} !important`,
        padding: '18px 24px 6px !important',
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Roboto, sans-serif !important',
      },

      // Backstage sidebar selection indicator (left bar)
      '& nav [class*="indicator"], & nav [class*="selectedIndicator"]': {
        backgroundColor: isDark ? 'rgba(77,163,255,0.5) !important' : 'rgba(0,120,212,0.4) !important',
      },

      // Settings group at bottom
      '& [class*="SidebarSpace"] + hr + [class*="SidebarGroup"]': {
        borderTop: `1px solid ${dividerColor} !important`,
        paddingTop: '8px !important',
        marginTop: '4px !important',
      },

      // Scrollbar
      '& ::-webkit-scrollbar': {
        width: 5,
      },
      '& ::-webkit-scrollbar-track': {
        background: 'transparent',
      },
      '& ::-webkit-scrollbar-thumb': {
        background: scrollThumb,
        borderRadius: 3,
      },
      '& ::-webkit-scrollbar-thumb:hover': {
        background: isDark ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.14)',
      },
    },
    contentWrapper: {
      position: 'fixed' as const,
      top: 0,
      right: 0,
      bottom: 0,
      left: 224,
      display: 'flex',
      flexDirection: 'column' as const,
      overflow: 'hidden',
      background: isDark ? '#1a1a1a' : '#f7f8fa',
      borderLeft: `1px solid ${sidebarBorder}`,
      zIndex: 1,
    },
    contentArea: {
      flex: 1,
      overflow: 'auto',
      minHeight: 0,
      scrollbarWidth: 'thin' as any,
      msOverflowStyle: 'auto' as any,
      '&::-webkit-scrollbar': {
        width: 6,
      },
      '&::-webkit-scrollbar-track': {
        background: 'transparent',
      },
      '&::-webkit-scrollbar-thumb': {
        background: scrollThumb,
        borderRadius: 3,
      },
      '&::-webkit-scrollbar-thumb:hover': {
        background: isDark ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.14)',
      },
      '& main': {
        scrollbarWidth: 'thin' as any,
        msOverflowStyle: 'auto' as any,
        '&::-webkit-scrollbar': {
          width: 6,
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: scrollThumb,
          borderRadius: 3,
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: isDark ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.14)',
        },
      },
    },
  };
});

export const Root = ({ children }: PropsWithChildren<{}>) => {
  const classes = useSidebarStyles();
  const location = useLocation();
  const configApi = useApi(configApiRef);
  const sfr = configApi.getOptionalConfig('app.slowFeaturesReveal');

  // Set aria-current on matching submenu links (needed for production where class names are minified)
  // Uses MutationObserver because submenu links are conditionally rendered on hover
  useEffect(() => {
    const wrapper = document.querySelector('[data-testid="item-with-submenu"]');
    if (!wrapper) return;

    const setAriaCurrent = () => {
      const links = wrapper.querySelectorAll<HTMLAnchorElement>('a[href]');
      links.forEach(link => {
        const href = link.getAttribute('href') || '';
        if (location.pathname === href || location.pathname.startsWith(`${href  }/`)) {
          link.setAttribute('aria-current', 'page');
        } else {
          link.removeAttribute('aria-current');
        }
      });
    };

    setAriaCurrent();
    const observer = new MutationObserver(setAriaCurrent);
    observer.observe(wrapper, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [location.pathname]);

  const getFeature = (key: string) => ({
    enabled: sfr?.getOptionalConfig(key)?.getOptionalBoolean('enabled') ?? false,
    place: sfr?.getOptionalConfig(key)?.getOptionalNumber('place') ?? 999,
  });

  const features = {
    search: getFeature('search'),
    home: getFeature('home'),
    marketplace: getFeature('marketplace'),
    harness: getFeature('harness'),
    quay: getFeature('quay'),
    subscriptions: getFeature('subscriptions'),
    catalog: getFeature('catalog'),
    announcements: getFeature('announcements'),
    links: getFeature('links'),
    documentation: getFeature('documentation'),
    ideas: getFeature('ideas'),
    topology: getFeature('topology'),
    services: getFeature('services'),
    techRadar: getFeature('techRadar'),
    notifications: getFeature('notifications'),
  };

  // true  → partial menu (only flagged items shown)
  // false → full menu (every page visible)
  const { notifications: notificationsFeature, ...sidebarFeatures } = features;
  const partialMenu = Object.values(sidebarFeatures).some(f => f.enabled);
  const showNotifications = notificationsFeature.enabled;

  return (
    <SidebarPage>
      <Box className={classes.sidebarWrapper}>
        {/* Default drawerWidthOpen (202px) truncates longer submenu item labels, e.g. "Service Registry Builder". */}
        <Sidebar submenuOptions={{ drawerWidthOpen: 260 }}>
          <SidebarLogo />
          <SidebarDivider />
          {partialMenu
            ? <SidebarPartial features={sidebarFeatures} showNotifications={showNotifications} />
            : <SidebarFull showNotifications={showNotifications} />
          }
          <SidebarSpace />
          <SidebarDivider />
          <SidebarGroup label="Settings" icon={<UserSettingsSignInAvatar />} to="/settings">
            <SidebarSettings />
          </SidebarGroup>
        </Sidebar>
      </Box>
      <Box className={classes.contentWrapper}>
        <PortalHeader />
        <Box className={classes.contentArea}>
          {children}
        </Box>
      </Box>
    </SidebarPage>
  );
};
